import type { CustomerApiClient } from '../api-client.ts';
import { Button, Card, EmptyState, SellerBadge } from '@beautyburst/ui';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { CartAddFeedback, useCartAddFeedback } from '../cart-add-feedback.tsx';
import { customerHref } from '../base-path.ts';

type FavoritesPage = Awaited<ReturnType<CustomerApiClient['favorites']['page']>>;

function availabilityLabel(state: string): string {
  if (state === 'AVAILABLE') return 'Available';
  if (state === 'OUT_OF_STOCK') return 'Out of stock';
  return 'Listing unavailable';
}

export interface FavoritesScreenProps {
  page: FavoritesPage;
  client: Pick<CustomerApiClient, 'favorites'> & Partial<Pick<CustomerApiClient, 'cart'>>;
  onChanged?: () => void | Promise<void>;
}

/** Favorites use the current server availability, not a stale saved-item snapshot. */
export function FavoritesScreen({ page, client, onChanged }: FavoritesScreenProps) {
  return (
    <section aria-labelledby="favorites-title">
      <h1
        id="favorites-title"
        style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}
      >
        Favorites
      </h1>

      <section aria-labelledby="favorite-products-title">
        <h2
          id="favorite-products-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Products
        </h2>
        {page.products.length === 0 ? (
          <EmptyState
            title="No favorite products yet"
            description="Save products you want to find again."
            action={<a href={customerHref('/categories')}>Browse beauty products</a>}
          />
        ) : (
          <ul
            aria-label="Favorite products"
            style={{ display: 'grid', gap: space[3], listStyle: 'none', padding: 0 }}
          >
            {page.products.map((product) => (
              <FavoriteProductRow
                key={product.id}
                product={product}
                client={client}
                onChanged={onChanged}
              />
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="favorite-stores-title" style={{ marginTop: space[8] }}>
        <h2
          id="favorite-stores-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Stores
        </h2>
        {page.stores.length === 0 ? (
          <EmptyState
            title="No favorite stores yet"
            description="Save local stores to find them again."
            action={<a href={customerHref('/stores')}>Browse nearby stores</a>}
          />
        ) : (
          <ul
            aria-label="Favorite stores"
            style={{ display: 'grid', gap: space[3], listStyle: 'none', padding: 0 }}
          >
            {page.stores.map((store) => (
              <li key={store.id}>
                <Card>
                  <h3
                    style={{
                      ...typeStyle('headingSm'),
                      color: semanticColor.textPrimary,
                      marginTop: 0,
                    }}
                  >
                    {store.seller_display_name ?? 'Store details unavailable'}
                  </h3>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      void client.favorites.unfavoriteStore(store.id).then(onChanged);
                    }}
                  >
                    Remove favorite
                  </Button>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

function FavoriteProductRow({
  product,
  client,
  onChanged,
}: {
  product: FavoritesPage['products'][number];
  client: Pick<CustomerApiClient, 'favorites'> & Partial<Pick<CustomerApiClient, 'cart'>>;
  onChanged?: () => void | Promise<void>;
}) {
  const { addToBag, dismiss, feedback } = useCartAddFeedback(
    client.cart === undefined ? undefined : { cart: client.cart },
  );
  const canAddToBag = product.availability === 'AVAILABLE' && product.listing_id !== null;
  const isDirect = product.ownership_type === 'BEAUTY_BURST_DIRECT';
  return (
    <li>
      <Card>
        <h3
          style={{
            ...typeStyle('headingSm'),
            color: semanticColor.textPrimary,
            marginTop: 0,
          }}
        >
          {product.product_name ?? 'Product details unavailable'}
        </h3>
        <SellerBadge retailerName={isDirect ? null : product.seller_display_name} />
        <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
          {availabilityLabel(product.availability)}
        </p>
        {product.price_cents === null ? (
          <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
            Current price unavailable
          </p>
        ) : (
          <p style={{ ...typeStyle('body'), color: semanticColor.textPrimary }}>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(product.price_cents / 100)}
          </p>
        )}
        <Button
          variant="primary"
          disabled={!canAddToBag}
          onClick={() => {
            if (product.listing_id !== null)
              void addToBag({ listingId: product.listing_id, quantity: 1 });
          }}
        >
          Add to Bag
        </Button>
        <CartAddFeedback feedback={feedback} onDismiss={dismiss} />
        <Button
          variant="ghost"
          onClick={() => {
            void client.favorites.unfavoriteProduct(product.id).then(onChanged);
          }}
        >
          Remove favorite
        </Button>
      </Card>
    </li>
  );
}
